<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <Mail class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Subscription invoice emails
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="space-y-4">
            <!-- Email list -->
            <div v-if="emails.length" class="flex flex-col gap-2.5">
                <div
                    v-for="(email, index) in emails"
                    :key="index"
                    class="flex h-10 min-h-10 items-center justify-between bg-new-neutral-50 px-3"
                >
                    <span class="text-xs text-foreground">{{ email }}</span>
                    <Button
                        variant="link"
                        size="sm"
                        class="text-new-blue-500"
                        @click="openDeleteDialog(index)"
                    >
                        Remove
                    </Button>
                </div>
            </div>

            <!-- Add email form -->
            <div class="flex gap-0">
                <Input
                    v-model="newEmail"
                    type="email"
                    placeholder="info@example.com"
                    class="flex-1"
                    :disabled="emails.length >= 5"
                    @keydown.enter.prevent="addEmail"
                />
                <Button
                    :disabled="!newEmail || emails.length >= 5"
                    @click="addEmail"
                >
                    Add e-mail
                </Button>
            </div>

            <p class="text-xs text-muted-foreground">
                You can receive subscription invoices by e-mail. Choose the e-mail addresses where you'd like to receive them. You can add up to 5 e-mails.
            </p>
        </CardContent>
    </Card>

    <Dialog :open="pendingIndex !== null" @update:open="(val) => !val && (pendingIndex = null)">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Remove invoice email</DialogTitle>
                <DialogDescription>
                    Stop sending subscription invoices to
                    <span class="font-medium text-foreground">{{ pendingEmail }}</span>?
                </DialogDescription>
            </DialogHeader>
            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" @click="pendingIndex = null">Cancel</Button>
                <Button size="lg" variant="destructive" @click="confirmRemove">Remove</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import { Mail } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';

const globalProps = computed(() => usePage<ITopolPageProps>().props);

const emails = ref<string[]>(
    globalProps.value.team.features?.recurring_invoice_emails ?? []
);
const newEmail = ref('');

const saveEmails = () => {
    router.put(
        route('settings.recurring-invoice-emails.update'),
        { emails: emails.value },
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success('Invoice emails updated');
            },
            onError: () => {
                toast.error('Failed to update invoice emails');
            },
        }
    );
};

const addEmail = () => {
    const email = newEmail.value.trim();
    if (!email) return;

    if (emails.value.length >= 5) {
        toast.error('You can add up to 5 emails');
        return;
    }

    if (emails.value.includes(email)) {
        toast.error('This email is already added');
        return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        toast.error('Please enter a valid email address');
        return;
    }

    emails.value.push(email);
    newEmail.value = '';
    saveEmails();
};

const pendingIndex = ref<number | null>(null);
const pendingEmail = computed(() =>
    pendingIndex.value !== null ? emails.value[pendingIndex.value] : '',
);

const openDeleteDialog = (index: number) => {
    pendingIndex.value = index;
};

const confirmRemove = () => {
    if (pendingIndex.value === null) return;
    emails.value.splice(pendingIndex.value, 1);
    pendingIndex.value = null;
    saveEmails();
};
</script>
